import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Phone, Play, LogOut, BookOpen, Clock, AlertCircle, User, Calendar, CheckCircle2, Lock, TimerReset } from 'lucide-react';

/* ─── tiny countdown hook ─── */
function useCountdown(targetMs) {
  const [diff, setDiff] = useState(() => Math.max(0, targetMs - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setDiff(Math.max(0, targetMs - Date.now())), 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  return {
    hours:   Math.floor(diff / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
    expired: diff <= 0,
  };
}

export default function StudentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [testStatuses, setTestStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);
  const [examSettings, setExamSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Refs to always hold the latest values inside callbacks/effects without stale closures
  const acknowledgedRef = useRef(false);
  const navigateRef = useRef(navigate);

  // Keep refs in sync
  useEffect(() => { acknowledgedRef.current = acknowledged; }, [acknowledged]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  useEffect(() => {
    fetchTests();
    fetchExamSettings();
  }, []);

  const fetchExamSettings = async () => {
    try {
      const { data } = await api.get('/exam-settings');
      setExamSettings(data.data || null);
    } catch {} finally {
      setSettingsLoaded(true);
    }
  };

  const fetchTests = async () => {
    try {
      const { data } = await api.get('/tests');
      setTests(data.data);

      const statuses = {};
      await Promise.all(data.data.map(async (test) => {
        try {
          const { data: statusData } = await api.get(`/exam/status/${test._id}`);
          statuses[test._id] = statusData.data;
        } catch {}
      }));
      setTestStatuses(statuses);
    } catch (err) {
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  /* ── Exam window status (recomputed every second via useMemo) ── */
  const esStart = useMemo(
    () => examSettings?.startTime ? new Date(examSettings.startTime) : null,
    [examSettings]
  );
  const esEnd = useMemo(
    () => examSettings?.endTime ? new Date(examSettings.endTime) : null,
    [examSettings]
  );

  const countdown = useCountdown(esStart ? esStart.getTime() : 0);

  // windowStatus recalculates every second because countdown.seconds changes each tick
  const windowStatus = useMemo(() => {
    const now = new Date();
    if (!esStart) return 'no-schedule';
    if (now < esStart) return 'upcoming';
    if (!esEnd || now <= esEnd) return 'live';
    return 'ended';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esStart, esEnd, countdown.seconds]);

  /* ── Button handler — all validations in order ── */
  const handleStartExam = useCallback((testId) => {
    // 1. Acknowledgement gate — read from ref to get latest value synchronously
    if (!acknowledgedRef.current) {
      toast.error('Please tick the acknowledgement checkbox first');
      return;
    }

    // 2. Settings must have loaded before we can evaluate timing
    if (!settingsLoaded) {
      toast.error('Loading exam settings, please wait a moment.');
      return;
    }

    // 3. Student approval check
    if (user?.registrationStatus && user.registrationStatus !== 'Approved') {
      toast.error('Your registration is pending admin approval.');
      return;
    }

    // 4. Recompute window status fresh at click time (avoids any stale closure)
    const now = new Date();
    let liveWindowStatus = 'no-schedule';
    if (esStart) {
      if (now < esStart) liveWindowStatus = 'upcoming';
      else if (!esEnd || now <= esEnd) liveWindowStatus = 'live';
      else liveWindowStatus = 'ended';
    }

    if (liveWindowStatus === 'ended') {
      toast.error('Exam session has ended.');
      return;
    }

    if (liveWindowStatus === 'upcoming') {
      const h = String(countdown.hours).padStart(2, '0');
      const m = String(countdown.minutes).padStart(2, '0');
      const s = String(countdown.seconds).padStart(2, '0');
      toast.error(`Exam starts in ${h}:${m}:${s}. Please wait.`, { duration: 4000 });
      return;
    }

    // 5. If already has result → go to result page
    const status = testStatuses[testId];
    if (status?.hasResult) {
      navigate(`/student/result/${testId}`);
      return;
    }

    // 6. All checks passed — navigate immediately
    navigate(`/student/exam/${testId}`);
  }, [settingsLoaded, user, esStart, esEnd, countdown, testStatuses, navigate]);

  /* auto-navigate when countdown hits zero */
  useEffect(() => {
    if (!countdown.expired) return;
    // Recompute fresh — avoids stale windowStatus from previous render
    const now = new Date();
    const isNowLive = esStart && now >= esStart && (!esEnd || now <= esEnd);
    if (isNowLive && tests.length > 0 && acknowledgedRef.current) {
      toast.success('Exam is now live! Starting...');
      navigateRef.current(`/student/exam/${tests[0]._id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown.expired]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const photoUrl = user?.photo || null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full overflow-hidden shrink-0 shadow-sm border border-gray-100">
              <img src="/bits-logo.png" alt="BITS Logo" className="w-8 h-8 object-contain" />
            </div>
            <span className="text-black font-bold">EAMCET Portal</span>
          </div>
          <button onClick={handleLogout}
            className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors text-sm">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* ─── TWO-PANEL LAYOUT ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── LEFT PANEL: Student Identity ── */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 text-center">
              {/* Photo */}
              <div className="flex justify-center mb-4">
                {photoUrl ? (
                  <img src={photoUrl} alt="Profile" className="w-28 h-28 rounded-full object-cover border-4 border-gray-100" />
                ) : (
                  <div className="w-28 h-28 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 text-4xl font-bold border-4 border-gray-200">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Hall Ticket */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Hall Ticket Number</p>
                <p className="text-lg font-bold font-mono text-black tracking-wider">{user?.hallTicketNumber || '—'}</p>
              </div>

              {/* Name */}
              <h2 className="text-xl font-bold text-black">{user?.name}</h2>
              <div className="bg-green-50 border border-green-200 text-green-700 text-xs px-3 py-1 rounded-full font-medium inline-block mt-2">
                ✓ Verified Student
              </div>
            </div>
          </div>

          {/* ── RIGHT PANEL: Details + Instructions + Exam ── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Section 1: Student Details */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
                <User size={18} /> Student Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Full Name</p>
                  <p className="text-sm font-medium text-black">{user?.name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Father's Name</p>
                  <p className="text-sm font-medium text-black">{user?.fatherName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Date of Birth</p>
                  <p className="text-sm font-medium text-black flex items-center gap-1.5">
                    <Calendar size={14} className="text-gray-400" />
                    {user?.dob ? new Date(user.dob).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Phone Number</p>
                  <p className="text-sm font-medium text-black flex items-center gap-1.5">
                    <Phone size={14} className="text-gray-400" />
                    {user?.phone || '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Section 2: Exam Instructions */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
                <AlertCircle size={18} /> Exam Instructions
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex gap-2"><span className="text-gray-400">1.</span> Each test has <strong>160 questions</strong> (80 Math + 40 Physics + 40 Chemistry).</li>
                <li className="flex gap-2"><span className="text-gray-400">2.</span> Duration: <strong>180 minutes</strong> (3 hours).</li>
                <li className="flex gap-2"><span className="text-gray-400">3.</span> Marking: <strong>+1 for correct, 0 for incorrect or unattempted — No Negative Marking</strong>.</li>
                <li className="flex gap-2"><span className="text-gray-400">4.</span> You can navigate between questions and mark them for review.</li>
                <li className="flex gap-2"><span className="text-gray-400">5.</span> The exam will auto-submit when time expires.</li>
                <li className="flex gap-2"><span className="text-gray-400">6.</span> <strong>Do not refresh</strong> the page during the exam — your progress is auto-saved.</li>
                <li className="flex gap-2"><span className="text-gray-400">7.</span> Once submitted, you <strong>cannot retake</strong> the test.</li>
                <li className="flex gap-2"><span className="text-gray-400">8.</span> Ensure a stable internet connection before starting the exam.</li>
              </ul>

              {/* Acknowledgement Checkbox */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                  />
                  <span className="text-sm text-gray-700">
                    I have read and understood all the instructions. I agree to abide by the examination rules.
                  </span>
                </label>
              </div>
            </div>

            {/* ── Exam Window Status Banner ── */}
            {settingsLoaded && (
              <>
                {windowStatus === 'upcoming' && esStart && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4">
                    <TimerReset className="text-amber-500 shrink-0" size={28} />
                    <div className="flex-1">
                      <p className="font-bold text-amber-800 text-sm">Exam starts in</p>
                      <p className="text-2xl font-mono font-extrabold text-amber-900 tracking-widest mt-0.5">
                        {String(countdown.hours).padStart(2,'0')}:{String(countdown.minutes).padStart(2,'0')}:{String(countdown.seconds).padStart(2,'0')}
                      </p>
                      <p className="text-xs text-amber-600 mt-1">Opens at {esStart.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                )}
                {windowStatus === 'live' && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                    <CheckCircle2 className="text-green-500 shrink-0" size={24} />
                    <div>
                      <p className="font-bold text-green-800 text-sm">✅ Exam is Live</p>
                      {esEnd && <p className="text-xs text-green-600">Closes at {esEnd.toLocaleString('en-IN')}</p>}
                    </div>
                  </div>
                )}
                {windowStatus === 'ended' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                    <Lock className="text-red-500 shrink-0" size={24} />
                    <div>
                      <p className="font-bold text-red-800 text-sm">Exam Session Ended</p>
                      <p className="text-xs text-red-600">This exam window is now closed.</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Available Tests */}
            <div>
              <h3 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
                <BookOpen size={18} /> Available Tests
              </h3>

              {loading ? (
                <div className="flex justify-center py-16"><div className="loader"></div></div>
              ) : tests.length === 0 ? (
                <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-8 text-center text-gray-500">
                  <BookOpen size={48} className="mx-auto mb-2 opacity-40" />
                  <p>No tests available yet. Check back later.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {tests.map(test => {
                    const status = testStatuses[test._id];
                    const hasResult = status?.hasResult;
                    const isInProgress = status?.status === 'in-progress';

                    return (
                      <div key={test._id} className="bg-white border border-gray-200 shadow-sm rounded-xl p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-bold text-black">{test.title}</h4>
                              {hasResult && (
                                <span className="bg-green-50 text-green-700 border border-green-200 text-xs px-2 py-0.5 rounded-full">Completed</span>
                              )}
                              {isInProgress && !hasResult && (
                                <span className="bg-gray-100 text-gray-700 border border-gray-200 text-xs px-2 py-0.5 rounded-full">In Progress</span>
                              )}
                            </div>
                            {test.description && <p className="text-gray-500 text-sm mb-3">{test.description}</p>}
                            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <BookOpen size={13} /> {test.totalQuestions} Questions
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={13} /> {test.duration} minutes
                              </span>
                            </div>
                          </div>

                          <button
                            onClick={() => handleStartExam(test._id)}
                            disabled={(!acknowledged && !hasResult) || windowStatus === 'ended'}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all flex-shrink-0 ${
                              hasResult
                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                                : windowStatus === 'ended'
                                  ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                  : acknowledged
                                    ? 'bg-black text-white hover:bg-gray-800 active:scale-95'
                                    : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                            }`}
                          >
                            <Play size={16} />
                            {hasResult ? 'View Result' : windowStatus === 'ended' ? 'Closed' : isInProgress ? 'Resume Exam' : 'Start Exam'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
